import React from "react";
import { Check, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { mediaUrl } from "../../lib/api";
import { eventDate, photo } from "./racex";
import { OfferLink, RacexPhone } from "./RacexShell";

const FACTS = [
  { value: "650", unit: "HP", title: "V8 RACE CAR", detail: "The real thing. Not a hot hatch." },
  { value: "9", unit: "LAPS", title: "YOU AT THE WHEEL", detail: "You drive it. Nobody else." },
  { value: "10", unit: "%", title: "NQ SPRINGS ONLY", detail: "Exclusive pricing for our people." },
  { value: "1", unit: "DAY", title: "YOU'LL TALK ABOUT", detail: "Bring the crew or the family." },
];

const PROMISES = [
  "Full driver briefing — no experience needed",
  "Bring mates, family or the whole work crew",
  "Local team, local contact, zero runaround",
];

export const RacexDetails = ({ event }) => <>
  <section id="racex-experience" className="rx-facts-section">
    <div className="rx-container rx-facts">{FACTS.map((f, i) => (
      <div key={f.title} className="rx-fact" data-testid={`racex-fact-${i}`}>
        <span className="rx-fact-index" aria-hidden="true">0{i + 1}</span>
        <strong className="rx-fact-value">{f.value}<em>{f.unit}</em></strong>
        <h2>{f.title}</h2>
        <p>{f.detail}</p>
      </div>
    ))}</div>
  </section>

  <section className="rx-container rx-story">
    <div className="rx-story-copy">
      <p className="rx-kicker"><span className="rx-racing-slashes" aria-hidden="true"><i /><i /><i /></span>LEAVE THE USUAL SUNDAY BEHIND</p>
      <h2 data-testid="racex-experience-heading">Not another lunch.<br /><em>Not another gift card.</em></h2>
      <p className="rx-story-lead">Give yourself — or your favourite people — a day with a lot more horsepower. Get behind the wheel, soak up the race-day atmosphere, and walk away with a story worth telling.</p>
      <ul className="rx-story-list">{PROMISES.map((p) => <li key={p}><Check size={17} strokeWidth={3} aria-hidden="true" />{p}</li>)}</ul>
      {event?.accepting_enquiries && <OfferLink event={event} placement="story" />}
    </div>
    <figure className="rx-story-media">
      <img src={mediaUrl(photo("25"), 1000)} alt="Race-day guests in the pit area with their helmets" loading="lazy" decoding="async" data-testid="racex-story-image" />
      <figcaption><strong>Your NQ Springs team</strong><span>is the place to start.</span></figcaption>
    </figure>
  </section>
</>;

// Rendered separately so the questions sit after the enquiry, rather than ahead of the offer.
export const RacexFaq = ({ faq, event, contact }) => {
  if (!faq && !event) return null;
  const items = faq || [
    ["When and where is it?", `${eventDate(event.event_date)} in Townsville, with a semi-private afternoon session. ${event.venue || "NQ Springs will confirm the circuit and arrival details when you enquire."}`],
    ["Can I organise this for a group?", "Tell us how many drivers are interested. The event is promoted for staff, customers, friends and family. NQ Springs will check availability for your group."],
    ["Does an enquiry reserve my place?", "No. This is an availability request, not a confirmed booking. No payment is taken here. NQ Springs will contact you with the next steps."],
    ["What are the driver requirements?", event.driver_requirements || "Contact NQ Springs to confirm licence, age and participation requirements before booking."],
    ...(event.booking_terms ? [["What are the booking conditions?", event.booking_terms]] : []),
  ];
  return <section id="racex-faq" className="rx-container rx-faq">
    <div className="rx-faq-aside">
      <p className="rx-kicker"><span className="rx-racing-slashes" aria-hidden="true"><i /><i /><i /></span>THE PRACTICAL DETAILS</p>
      <h2 data-testid="racex-faq-title">Before you<br /><em>hit the track.</em></h2>
      <div className="rx-faq-card">
        <p><Phone size={18} aria-hidden="true" />Rather just ask us?</p>
        <RacexPhone contact={contact} placement="faq" />
        <span>NQ Springs, Townsville. Real people, straight answers.</span>
      </div>
    </div>
    <Accordion type="single" collapsible className="rx-faq-list">{items.map(([q, a], i) => (
      <AccordionItem value={`faq-${i}`} key={q}>
        <AccordionTrigger data-testid={`racex-faq-${i}-button`}><span className="rx-faq-number" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>{q}</AccordionTrigger>
        <AccordionContent data-testid={`racex-faq-${i}-answer`}>{a}</AccordionContent>
      </AccordionItem>
    ))}</Accordion>
  </section>;
};
